import { useTranslations } from 'next-intl';
import useFormatterPrice from '@/src/utils/formatter-price';
import { useFormatter } from 'next-intl';
import useCurrencyConversion from '@/src/utils/useCurrencyConversion';
import getDefaultCurrency from '@/src/utils/getDefaultCurrency';
import { MdCheckCircle } from 'react-icons/md';
import { IoMdInformationCircle, IoMdPricetag } from 'react-icons/io';
import { IoBanOutline } from 'react-icons/io5';
import BlurredPrice from './BlurredPrice';
import getOption from '@/src/utils/getOption';
import VatInfoPopup from './VatInfoPopup';
import useIfDefaultCurrency from '@/src/utils/useIfDefaultCurrency';

export default function AuctionCurrentBid({
  bid,
  itemizedCosts,
  hasBid,
  isAuction = true,
  reserve = false,
  showPrice = true,
  sold = false,
  isPrivate = false,
  adStatus = 'active',
}: {
  bid: number;
  itemizedCosts?: any;
  hasBid?: boolean;
  isAuction?: boolean;
  reserve?: false | number;
  showPrice?: boolean;
  sold?: boolean;
  isPrivate?: boolean;
  adStatus?: string;
}) {
  const t = useTranslations('AuctionCurrentBid');
  const isDefaultCurrency = useIfDefaultCurrency();
  const convert = useCurrencyConversion();
  const formatterPrice = useFormatterPrice();
  const formatter = useFormatter();

  const totalCost = itemizedCosts?.total?.price_incl_vat;

  if (
    ['draft', 'user-draft'].includes(adStatus) ||
    (['closed'].includes(adStatus) && !sold)
  ) {
    return (
      <div className='m-6 mb-0 flex items-center justify-center gap-2 rounded bg-dark p-3 text-center text-2xl font-bold text-white'>
        <span className='shrink-0'>
          <IoBanOutline />
        </span>
        {t('adInactive')}
      </div>
    );
  }

  if (!showPrice && !isAuction) {
    return (
      <div className='m-6 mb-0 flex items-center justify-center gap-2 rounded bg-dark p-3 text-center text-2xl font-bold text-white'>
        <span className='shrink-0'>
          <IoMdPricetag />
        </span>
        {t('sold')}
      </div>
    );
  } else if (!showPrice) {
    return;
  }

  return (
    <div className='flex flex-wrap justify-center gap-8 p-6 py-4 text-center'>
      <div className='flex w-full flex-col'>
        <span className='text-sm uppercase'>
          {isAuction && <>{hasBid ? t('highestBid') : t('startingPrice')}</>}{' '}
          <span className='font-bold'>({getDefaultCurrency()})</span>
        </span>
        <span className='text-5xl font-semibold'>
          {showPrice ? formatter.number(bid) : <BlurredPrice />}
        </span>
        {!isDefaultCurrency && !!convert(bid) && showPrice && (
          <span className='text-xl'>
            {t('approx', { price: formatterPrice(convert(bid), undefined, 'EUR') })}
          </span>
        )}
        {showPrice && totalCost && (
          <div className='relative flex items-center justify-center gap-1 uppercase'>
            {t('includingVat')}:{' '}
            {formatterPrice(totalCost, String(getOption('DEFAULT_LOCALE')))}
            <VatInfoPopup itemizedCosts={itemizedCosts} isPrivate={isPrivate} />
          </div>
        )}
        {reserve && (
          <>
            {reserve <= bid ? (
              <div className='mt-4 flex items-center gap-1 self-center rounded-xl bg-positive/30 px-6 py-1 text-xs'>
                <span className='text-lg text-positive'>
                  <MdCheckCircle />
                </span>
                {t('reserveMet')}
              </div>
            ) : (
              <div className='mt-4 flex items-center gap-1 self-center rounded-xl bg-brand-dark px-6 py-1 text-xs'>
                <span className='text-lg text-brand-alt'>
                  <IoMdInformationCircle />
                </span>
                {t('reserveNotMet')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
